// ═══════════════════════════════════════════════════════════════════
//  modules/ah/AhPage.js
//  AhPage · AhResume · AhPaiements
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { argent, dateCourte, erreurDe, etatInfo, horodate, nomObjet, nombre } from "./ah";
import { AhAnnonce } from "./AhAnnonce";
import { AhAnnonces } from "./AhAnnonces";
import { AhBoites, MessageErreur } from "./AhBoites";
import { AhJournal } from "./AhJournal";

export function AhPage({ token, user }) {
  const [vue, setVue] = useState("annonces");
  const [annonce, setAnnonce] = useState(null);   // id ouvert, ou null
  const [resume, setResume] = useState(null);

  // Les paiements sont réservés à admin et super_admin : ce n'est pas un
  // outil de modération mais une alarme d'exploitation. Le backend refuse
  // de toute façon — l'interface cache, elle ne protège pas.
  const peutVoirLesPaiements = !!user && (user.role === "super_admin" || user.role === "admin");

  const charger = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/ah/resume`, { headers: { Authorization: `Bearer ${token}` } });
      setResume(await res.json());
    } catch (err) { console.error(err); }
  }, [token]);
  useEffect(() => { charger(); }, [charger]);

  if (resume?.unavailable) return (
    <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
      ⏳ Les tables <code>oa_*</code> n'existent pas sur cette base. Elles sont créées au premier
      démarrage du plugin OrialisAH.
    </div>
  );

  const vues = [
    { id: "annonces", label: "🏷️ Annonces" },
    { id: "journal", label: "📜 Journal" },
    { id: "boites", label: "📮 Boîtes de retrait" },
    ...(peutVoirLesPaiements ? [{ id: "paiements", label: "💸 Paiements en attente" }] : []),
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ color: THEME.text, fontSize: 22, fontWeight: 800, margin: 0 }}>🏪 Hôtel des ventes</h1>
        <span style={{ color: THEME.textMuted, fontSize: 12 }}>lecture seule — les actions passent par <code>/ah admin</code> en jeu</span>
      </div>

      <AhResume resume={resume} />

      {annonce !== null ? (
        <AhAnnonce id={annonce} token={token} onFermer={() => { setAnnonce(null); charger(); }} />
      ) : (
        <>
          <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${THEME.border}` }}>
            {vues.map((v) => (
              <button key={v.id} onClick={() => setVue(v.id)}
                style={{
                  background: vue === v.id ? THEME.panel : "transparent",
                  border: `1px solid ${vue === v.id ? THEME.border : "transparent"}`,
                  borderBottom: vue === v.id ? `1px solid ${THEME.panel}` : `1px solid ${THEME.border}`,
                  color: vue === v.id ? THEME.text : THEME.textMuted,
                  borderRadius: "6px 6px 0 0", padding: "8px 16px", cursor: "pointer",
                  fontSize: 13, fontWeight: vue === v.id ? 600 : 400, marginBottom: -1,
                }}>
                {v.label}
                {v.id === "boites" && resume?.boite?.lignes > 0 && (
                  <span style={{ background: THEME.amberDim, color: THEME.amber, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, marginLeft: 6 }}>
                    {resume.boite.lignes}
                  </span>
                )}
                {v.id === "paiements" && resume?.dette?.lignes > 0 && (
                  <span style={{ background: THEME.redDim, color: THEME.red, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, marginLeft: 6 }}>
                    {resume.dette.lignes}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: "0 8px 8px 8px", padding: 20, minHeight: 240 }}>
            {vue === "annonces" && <AhAnnonces token={token} onOuvrirAnnonce={setAnnonce} />}
            {vue === "journal" && <AhJournal token={token} onOuvrirAnnonce={setAnnonce} />}
            {vue === "boites" && <AhBoites token={token} />}
            {vue === "paiements" && <AhPaiements token={token} onOuvrirAnnonce={setAnnonce} />}
          </div>
        </>
      )}
    </div>
  );
}

// ── Le bandeau de chiffres ─────────────────────────────────────────────
function AhResume({ resume }) {
  if (!resume) return <div style={{ color: THEME.textMuted, fontSize: 12 }}>Chargement des chiffres...</div>;
  // Le bandeau de chiffres ne doit jamais emporter la page : s'il échoue,
  // les onglets en dessous restent utilisables.
  const erreur = erreurDe(resume);
  if (erreur || !resume.carnet) return <MessageErreur texte={erreur || "Réponse incomplète du serveur."} />;

  const cases = [
    ["En vente", `${nombre(resume.carnet.annonces)} annonces`, `${nombre(resume.carnet.unites)} unités · ${nombre(resume.carnet.vendeurs)} vendeurs`, THEME.text],
    ["Valeur du carnet", argent(resume.carnet.valeur), "si tout partait au prix affiché", THEME.gold],
    ["Ventes aujourd'hui", argent(resume.ventes_jour.montant), `${nombre(resume.ventes_jour.lignes)} ligne(s)`, THEME.emerald],
    ["Ventes sur 7 jours", argent(resume.ventes_7j.montant), `${nombre(resume.ventes_7j.lignes)} lignes · ${nombre(resume.ventes_7j.unites)} unités`, THEME.emerald],
    ["Objets en boîte", `${nombre(resume.boite.lignes)}`, `${nombre(resume.boite.unites)} unités · ${nombre(resume.boite.joueurs)} joueurs`, THEME.text],
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* La dette passe AVANT les chiffres flatteurs, et seulement si elle */}
      {/* existe. Un compteur « 0 » de plus dans une grille ne se remarque   */}
      {/* pas ; un bandeau rouge, si.                                        */}
      {resume.dette.lignes > 0 && (
        <div style={{ background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
          <span style={{ color: THEME.red, fontWeight: 700 }}>
            ⛔ {nombre(resume.dette.lignes)} paiement(s) non versé(s) — {argent(resume.dette.montant)}
          </span>
          <div style={{ color: THEME.textMuted, fontSize: 11, marginTop: 4, lineHeight: 1.6 }}>
            Des vendeurs attendent leur argent. Le plugin rejoue ces lignes au démarrage :
            si elles sont encore là après un redémarrage du serveur de jeu, le rejeu ne marche pas.
            {resume.dette.plus_ancienne && <> La plus ancienne date du {dateCourte(resume.dette.plus_ancienne)}.</>}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        {cases.map(([label, valeur, sous, couleur]) => (
          <div key={label} style={{ background: THEME.surface, borderRadius: 6, padding: "10px 14px" }}>
            <div style={{ color: THEME.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>{label.toUpperCase()}</div>
            <div style={{ color: couleur, fontSize: 15, fontWeight: 700 }}>{valeur}</div>
            <div style={{ color: THEME.textDim, fontSize: 10, marginTop: 2 }}>{sous}</div>
          </div>
        ))}
      </div>

      {resume.etats && Object.keys(resume.etats).length > 0 && (
        <div style={{ color: THEME.textDim, fontSize: 11 }}>
          Toutes annonces confondues :{" "}
          {/* etatInfo, pas e.toLowerCase() : « EPUISEE » rendu tel quel */}
          {/* donne « epuisee » sans accent, et ça se voit.               */}
          {Object.entries(resume.etats).map(([e, n], i) => (
            <span key={e}>{i > 0 && " · "}{nombre(n)} {etatInfo(e).label.toLowerCase()}{n > 1 ? "s" : ""}</span>
          ))}
          {" — "}une annonce n'est jamais supprimée, elle change d'état.
        </div>
      )}
    </div>
  );
}

// ── Les dettes ─────────────────────────────────────────────────────────
function AhPaiements({ token, onOuvrirAnnonce }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/ah/paiements`, { headers: { Authorization: `Bearer ${token}` } });
        setData(await res.json());
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
  }, [token]);

  if (loading) return <div style={{ color: THEME.textMuted }}>Chargement...</div>;
  const erreur = erreurDe(data);
  if (erreur || !data.total) return <MessageErreur texte={erreur || "Réponse incomplète du serveur."} />;

  if (data.lignes.length === 0) return (
    <div style={{ color: THEME.emerald, background: THEME.emeraldDim, border: `1px solid ${THEME.emerald}`, borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
      ✅ Aucun paiement en attente. Tous les vendeurs ont été crédités.
      <div style={{ color: THEME.textMuted, fontSize: 11, marginTop: 6, lineHeight: 1.6 }}>
        C'est l'état normal, et c'est le seul compteur du module qui doit rester à zéro.
        Une ligne écrite ici est une dette : elle est versée dans la foulée de la vente, ou
        rejouée au démarrage suivant si le serveur est tombé entre-temps.
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ color: THEME.red, fontSize: 13, fontWeight: 700 }}>
        {nombre(data.total.lignes)} paiement(s) non versé(s) — {argent(data.total.montant)}
      </div>
      {data.tronque && (
        <div style={{ color: THEME.amber, fontSize: 11 }}>
          Seules les 1 000 plus anciennes lignes sont affichées. À ce volume, le problème n'est plus l'affichage.
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 700 }}>
          <thead>
            <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
              {["Vendu le", "Bénéficiaire", "Montant net", "Taxe", "Objet", "Acheteur", "Serveur"].map((h) => (
                <th key={h} style={{ padding: "6px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.lignes.map((l) => (
              <tr key={l.id} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                <td style={{ padding: "6px 10px", color: THEME.textMuted, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{horodate(l.vendue_le)}</td>
                <td style={{ padding: "6px 10px", color: THEME.text }}>{l.beneficiaire_pseudo || l.beneficiaire_uuid}</td>
                <td style={{ padding: "6px 10px", color: THEME.red, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{argent(l.montant_net)}</td>
                <td style={{ padding: "6px 10px", color: THEME.textDim, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{argent(l.taxe)}</td>
                <td style={{ padding: "6px 10px", color: THEME.text }}>
                  {l.annonce_id ? (
                    <button onClick={() => onOuvrirAnnonce(l.annonce_id)}
                      style={{ background: "none", border: "none", padding: 0, color: THEME.accent, cursor: "pointer", fontSize: 12, textAlign: "left", textDecoration: "underline dotted" }}>
                      {l.identifiant_lisible ? nomObjet(l.identifiant_lisible) : `annonce ${l.annonce_id}`}
                    </button>
                  ) : <span style={{ color: THEME.textDim }}>—</span>}
                  <span style={{ color: THEME.textDim }}> × {nombre(l.quantite)}</span>
                </td>
                <td style={{ padding: "6px 10px", color: THEME.textMuted }}>{l.acheteur_pseudo}</td>
                <td style={{ padding: "6px 10px", color: THEME.textMuted }}>{l.serveur}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
